import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Logo } from "@/components/Logo";
import { ShieldCheck, Sun, Heart, Users, MapPin, Share2, Copy, Check } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICONS = { "shield-check": ShieldCheck, sun: Sun, heart: Heart, users: Users, "map-pin": MapPin };
const LABELS = {
  vouch_received: "Vouch",
  gratitude: "Gratitude",
  kind_act: "Kind act",
  community_post: "Community",
  place_moment: "Crossed path",
};

const fmtDate = (iso) => {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" });
};

export default function LedgerShare() {
  const { licenseNo } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/ledger/public/${licenseNo}`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e.response?.data?.detail || "Ledger not found."));
  }, [licenseNo]);

  const share = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${data?.member_first_name}'s Kind Ledger — Wholesome`,
          text: "A living record of kindness on Wholesome.",
          url,
        });
      } catch (_) {}
    } else {
      navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Link copied.");
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (err)
    return (
      <div className="min-h-screen paper flex items-center justify-center text-ink/60" data-testid="ledger-share-error">
        {err}
      </div>
    );
  if (!data)
    return <div className="min-h-screen paper flex items-center justify-center text-ink/50">Loading…</div>;

  const s = data.stats || {};

  return (
    <div className="min-h-screen paper py-10 px-6" data-testid="ledger-share-page">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between">
          <Link to="/" data-testid="ledger-share-logo">
            <Logo />
          </Link>
          <button
            data-testid="ledger-share-btn"
            onClick={share}
            className="btn-pill bg-ink text-parchment hover:bg-ivy flex items-center gap-2 text-sm"
          >
            {copied ? <><Check size={14} /> Copied</> : <><Share2 size={14} /> Share</>}
          </button>
        </div>

        {/* Cover card — hero */}
        <div
          className="mt-8 card-paper p-10 relative overflow-hidden"
          style={{ border: "2px double #D4A574" }}
          data-testid="ledger-share-cover"
        >
          <div className="text-center ornament eyebrow text-ivy-500">The Kind Ledger of</div>
          <h1
            className="text-center font-display italic mt-3 text-ink"
            style={{ fontFamily: "Fraunces", fontSize: 56, fontWeight: 400, lineHeight: 1 }}
          >
            {data.member_first_name}.
          </h1>
          <div className="mt-3 text-center text-xs text-ochre-300 font-mono tracking-widest">
            LICENSE {data.license_no}
            {data.license_status === "licensed" && <span className="ml-2 text-ivy">· CHARACTER VERIFIED</span>}
          </div>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-8">
            {[
              { key: "vouches", label: "Vouches", icon: ShieldCheck },
              { key: "gratitude", label: "Gratitude", icon: Sun },
              { key: "kind_acts", label: "Kind acts", icon: Heart },
              { key: "community", label: "Community", icon: Users },
              { key: "places", label: "Places", icon: MapPin },
            ].map((it) => {
              const Icon = it.icon;
              return (
                <div
                  key={it.key}
                  className="text-center bg-parchment-50 rounded-2xl py-4 border border-amber-100"
                  data-testid={`share-stat-${it.key}`}
                >
                  <Icon size={16} className="mx-auto text-ivy" />
                  <div className="text-3xl font-display text-ink mt-1" style={{ fontFamily: "Fraunces" }}>
                    {s[it.key] || 0}
                  </div>
                  <div className="text-[10px] uppercase tracking-widest text-ink/50 mt-1">{it.label}</div>
                </div>
              );
            })}
          </div>

          <p className="mt-8 text-center italic text-ink/60 font-display" style={{ fontFamily: "Fraunces" }}>
            Kindness leaves a trail. This is {data.member_first_name}'s.
          </p>
        </div>

        {/* Recent events */}
        <div className="mt-10">
          <div className="eyebrow">Recent chapters</div>
          <div className="mt-4 space-y-4" data-testid="ledger-share-events">
            {(data.events || []).map((e) => {
              const Icon = ICONS[e.icon] || Heart;
              return (
                <div key={e.id} className="card-paper p-5" data-testid={`share-event-${e.id}`}>
                  <div className="flex items-baseline justify-between gap-3">
                    <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-ochre-300">
                      <Icon size={12} className="text-ivy" /> {LABELS[e.kind]}
                    </div>
                    <div className="text-xs text-ink/50 font-mono">{fmtDate(e.at)}</div>
                  </div>
                  <div className="mt-2 font-display text-lg text-ink" style={{ fontFamily: "Fraunces" }}>
                    {e.title}
                  </div>
                  {e.body && (
                    <blockquote
                      className="mt-2 text-ink/75 italic leading-relaxed"
                      style={{ fontFamily: "Fraunces" }}
                    >
                      &ldquo;{e.body}&rdquo;
                    </blockquote>
                  )}
                </div>
              );
            })}
            {(!data.events || data.events.length === 0) && (
              <div className="text-ink/50 italic" data-testid="ledger-share-empty">
                Their ledger is just beginning.
              </div>
            )}
          </div>
        </div>

        <div className="mt-12 text-center">
          <Link
            to="/"
            className="btn-pill bg-ivy text-parchment hover:bg-ivy-600 inline-flex items-center gap-2"
            data-testid="ledger-share-cta"
          >
            <Heart size={14} /> Get your own License to Love
          </Link>
          <div className="mt-4 text-xs text-ink/40 font-mono tracking-widest">
            WHOLESOME · CHARACTER-VERIFIED DATING
          </div>
        </div>
      </div>
    </div>
  );
}
